import { Switch } from "@headlessui/react";
import { CheckIcon } from "lucide-react";
import PetVaccinationStatus from "@/app/components/ui/Reusable/PetVaccinationStatus";
import { useUtils } from "@/app/hooks/useUtils";
import { isVaccinationServiceType } from "@/app/utils/bookingServiceSelection";
import editicon from "@/assets/images/edit.svg";
import smart from "@/assets/smart.svg";
import paidIcon from "@/assets/images/paidicon.svg";
import onHoldMembership from "@/assets/images/unpaid.svg";
import { IOfferCallout } from "@/api_framework/api_modals/Bookings";
import OfferCalloutBadge from "@/app/components/ui/Badges/OfferCalloutBadge";
import { formatAmount } from "@/app/components/routes/auth_routes/SlotsScreen/ComfirmationComponent/utils/confirmationHelpers";

interface CartItemsCardProps {
  completedBookingFlow: boolean;
  logPreviewEvent: (event_type: string, extra?: any) => void;
  setEditCartModalSignal: React.Dispatch<React.SetStateAction<number>>;
  selectedMembershipItem: any;
  isMembershipPaymentOnHold: boolean;
  selectedMembershipCouponName: string;
  showMembershipPayAtClinicToggle: boolean;
  membershipPayAtClinicSelected: boolean;
  handleMembershipPayAtClinicToggle: (checked: boolean) => void;
  selectedMembershipFinalPrice: number;
  membershipDiscountPercent: number;
  selectedMembershipOriginalPrice: number;
  isRescheduleMembershipBilled: boolean;
  isPrepaidPaymentCompleted: boolean;
  isRescheduleMembershipUnbilled: boolean;
  isReschedulePaymentPending: boolean;
  isPaidBooking: boolean;
  isPrepaidPaymentPending: boolean;
  serviceDisplayItem: any;
  serviceOfferCallout?: IOfferCallout | null;
  filters: any;
  selectedVaccineDisplayItems: any[];
  selectedAddOnItems: any[];
  selectedDiagnosticItems?: any[];
  selectedSurgeryItems: any[];
  selectedServiceTypeName: string;
}

const CartItemsCard = ({
  completedBookingFlow,
  logPreviewEvent,
  setEditCartModalSignal,
  selectedMembershipItem,
  isMembershipPaymentOnHold,
  selectedMembershipCouponName,
  showMembershipPayAtClinicToggle,
  membershipPayAtClinicSelected,
  handleMembershipPayAtClinicToggle,
  selectedMembershipFinalPrice,
  membershipDiscountPercent,
  selectedMembershipOriginalPrice,
  isRescheduleMembershipBilled,
  isPrepaidPaymentCompleted,
  isRescheduleMembershipUnbilled,
  isReschedulePaymentPending,
  isPaidBooking,
  isPrepaidPaymentPending,
  serviceDisplayItem,
  serviceOfferCallout,
  filters,
  selectedVaccineDisplayItems,
  selectedAddOnItems,
  selectedDiagnosticItems = [],
  selectedSurgeryItems,
  selectedServiceTypeName,
}: CartItemsCardProps) => {
  const { veticDateFormat } = useUtils();

  return (
    <section className="rounded-[10px] border border-[#E4E7ED] bg-white p-3 pb-0 shadow-[0_2px_10px_rgba(17,24,39,0.05)]">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[14px] font-semibold text-[#1F2431]">Items in Cart</h3>
        {!completedBookingFlow && (
          <button
            type="button"
            onClick={() => {
              logPreviewEvent("EDIT_CART_CLICK", { cta_text: "Edit Cart" });
              setEditCartModalSignal((prev) => prev + 1);
            }}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#DEE3EC] bg-white px-[12px] py-[8px] text-[14px] font-medium text-[#0068FF]"
          >
            <img src={editicon} alt="Edit" className="h-3.5 w-3.5" />
            Edit Cart
          </button>
        )}
      </div>

      {selectedMembershipItem && (
        <div
          className={`mb-0 rounded-[10px] px-[12px] py-[12px] ${isMembershipPaymentOnHold
            ? "border-2 border-[#00873C17]"
            : "border-2 border-[#00873C]"
            }`}
          style={{
            background: isMembershipPaymentOnHold
              ? "radial-gradient(ellipse at 50% 0%, rgba(246, 255, 164, 0.30) 0%, rgba(246, 255, 164, 0) 72%), linear-gradient(180deg, rgba(246, 255, 164, 0.10) 0%, rgba(246, 255, 164, 0.02) 100%)"
              : "radial-gradient(ellipse at 50% 0%, rgba(164, 255, 181, 0.45) 0%, rgba(164, 255, 181, 0) 72%), linear-gradient(111.32deg, #FFFFFF 29.47%, rgba(164, 255, 181, 0.3) 106.2%)",
          }}
        >
          <div className="flex flex-col gap-4 md:flex-row md:items-stretch md:justify-between">
            <div className="flex-1">
              <p className="text-[14px] font-medium text-[#222833]">{selectedMembershipItem?.name}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <p className="text-[12px] font-medium text-[#6D6D6D]">
                  {selectedMembershipItem?.duration
                    ? `${selectedMembershipItem.duration} min session`
                    : selectedMembershipItem?.description || "Membership plan"}
                </p>
                <span
                  className="px-2 py-0.5 text-xs font-medium flex gap-2"
                  style={{
                    background: "linear-gradient(111.32deg, #EAFFE6 29.47%, #C7FFD2 106.2%)",
                    borderRadius: "44px",
                  }}
                >
                  <img src={smart} alt="Smart Choice" className="h-3.5 w-3.5" />
                  <p className="text-[#079A27]">  Smart Choice</p>
                </span>
                {selectedMembershipCouponName && (
                  <span
                    className="px-2 py-0.5 text-[11px] font-semibold flex gap-1 items-center bg-[#EBF5FF] border border-[#CDE2FF] text-[#0052B4]"
                    style={{
                      borderRadius: "44px",
                    }}
                  >
                    <span>🎟️</span>
                    <p>{selectedMembershipCouponName}</p>
                  </span>
                )}
              </div>
              {selectedMembershipItem?.details && (
                <ul className="mt-3 space-y-1 text-[12px] font-medium text-[#016516]">
                  <li>{`• ${selectedMembershipItem.details}`}</li>
                </ul>
              )}
            </div>
            <div className="flex w-full flex-col items-end gap-3 md:w-auto md:self-stretch md:justify-between">
              <div className="flex w-full items-start justify-end gap-2 md:min-w-[230px]">
                {showMembershipPayAtClinicToggle && (
                  <div
                    className={`inline-flex items-center gap-[6px] rounded-full p-[2px] pl-[8px] ${membershipPayAtClinicSelected ? "bg-[#CDECD1]" : "bg-transparent"}`}
                  >
                    <p
                      className={`text-[12px] font-medium ${membershipPayAtClinicSelected ? "text-[#023F0F]" : "text-[#1F2431]"}`}
                    >
                      Pay at clinic
                    </p>
                    <Switch
                      checked={membershipPayAtClinicSelected}
                      onChange={handleMembershipPayAtClinicToggle}
                      disabled={completedBookingFlow}
                      className={`${membershipPayAtClinicSelected ? "bg-[#023F0F]" : "bg-[#DDE2E9]"} ${completedBookingFlow ? "cursor-not-allowed opacity-70" : ""} relative inline-flex h-[20px] w-[34px] items-center rounded-[44px] transition-colors`}
                    >
                      <span className="sr-only">Pay at clinic</span>
                      <span
                        className={`${membershipPayAtClinicSelected ? "translate-x-[16px]" : "translate-x-[4px]"} inline-flex h-[16px] w-[16px] transform items-center justify-center rounded-full bg-white transition`}
                      >
                        {membershipPayAtClinicSelected && (
                          <CheckIcon className="h-3.5 w-6 text-[#023F0F]" />
                        )}
                      </span>
                    </Switch>
                  </div>
                )}
                <div className="text-right">
                  <p className="text-sm font-bold text-[#1F2431]">
                    {formatAmount(selectedMembershipFinalPrice)}
                  </p>
                  {membershipDiscountPercent > 0 && (
                    <p className="text-sm text-[#8B92A1] line-through">
                      {formatAmount(selectedMembershipOriginalPrice)}
                    </p>
                  )}
                </div>
              </div>
              {isRescheduleMembershipBilled ? (
                <div className="inline-flex h-[30px] items-center gap-1.5 rounded-[8px] bg-[#FFFFFF] px-[8px] py-[4px]">
                  <img src={paidIcon} />
                  <span className="text-[14px] font-medium text-[#239A3D]">Paid</span>
                </div>
              ) : isPrepaidPaymentCompleted ? (
                <div className="inline-flex h-[30px] items-center gap-1.5 rounded-[8px] bg-[#FFFFFF] px-[8px] py-[4px]">
                  <img src={paidIcon} />
                  <span className="text-[14px] font-medium text-[#239A3D]">Paid</span>
                </div>
              ) : isMembershipPaymentOnHold ? (
                <div className="inline-flex h-[30px] items-center gap-1.5 rounded-[4px] bg-[#FFFFFF] px-[8px] py-[4px]">
                  <img src={onHoldMembership} className="h-4 w-4" />
                  <span className="text-[14px] font-medium text-[#533200]">Payment On-Hold</span>
                </div>
              ) : isRescheduleMembershipUnbilled || isReschedulePaymentPending || (isPaidBooking && isPrepaidPaymentPending) ? (
                <div className="inline-flex h-[30px] items-center gap-1.5 rounded-[4px] bg-[#FFFFFF] px-[8px] py-[4px]">
                  <img src={onHoldMembership} className="h-4 w-4" />
                  <span className="text-[14px] font-medium text-[#533200]">Unpaid</span>
                </div>
              ) : completedBookingFlow && membershipPayAtClinicSelected ? null : (
                <div className="flex h-[30px] max-w-[74px] items-center rounded-[6px] bg-white px-1">
                  <button type="button" className="cursor-not-allowed p-1 text-[#2A3140]" disabled>
                    -
                  </button>
                  <span className="px-3 text-sm font-semibold text-[#1F2431]">1</span>
                  <button type="button" className="cursor-not-allowed p-1 text-[#A6AFBE]" disabled>
                    +
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div>
        {serviceDisplayItem && (isVaccinationServiceType(filters?.service_id) || serviceDisplayItem.name?.toLowerCase() === "vaccination") && (
          <div className="px-[4px] py-[4px] bg-[#FFFFFF]">
            <p className="text-[14px] font-medium text-[#1F2431]">Vaccination</p>
          </div>
        )}
        {(() => {
          const isVaccineService = serviceDisplayItem && (isVaccinationServiceType(filters?.service_id) || serviceDisplayItem.name?.toLowerCase() === "vaccination");
          const cartItemsList = isVaccineService
            ? [...selectedVaccineDisplayItems, ...selectedAddOnItems, ...selectedDiagnosticItems]
            : [
                serviceDisplayItem,
                ...selectedVaccineDisplayItems,
                ...selectedAddOnItems,
                ...selectedDiagnosticItems,
                ...selectedSurgeryItems,
              ];
          const firstVaccineIndex = cartItemsList.findIndex((x) => x?.type === "vaccine");
          return cartItemsList.map((item, index) => {
            const originalPrice = Number(item?.price ?? 0);
            const finalPrice = Number(item?.final_price ?? originalPrice);
            const discountPercent = Number(item?.discount_percent ?? 0);
            const itemImageUrl =
              "image_url" in item && typeof item.image_url === "string" && item.image_url;
            const vaccineName = (item as any)?.vaccine_name;
            const isVaccine = item?.type === "vaccine";
            const showImageForVaccine = isVaccine && index === firstVaccineIndex;
            const isLastItem = index === cartItemsList.length - 1;
            return (
              <div
                key={`${item?.type}-${item?.id}-${index}`}
                className="flex bg-[#FFFFFF]"
              >
                {/* Left: Image or spacer */}
                <div className="flex shrink-0 items-center pl-[12px] py-[14px]">
                  {showImageForVaccine || (!isVaccine && itemImageUrl) ? (
                    itemImageUrl ? (
                      <img
                        src={itemImageUrl}
                        alt={vaccineName || item?.name || "item"}
                        className="h-[50px] w-[50px] object-cover"
                      />
                    ) : (
                      <div className="h-[50px] w-[50px] bg-[#F2F4F8]" />
                    )
                  ) : (
                    <div className="w-[50px] shrink-0" />
                  )}
                </div>

                {/* Right: Content block */}
                <div className={`flex flex-1 min-w-0 justify-between items-center gap-2 pr-[12px] pl-[10px] py-[14px] ${isLastItem ? "" : "border-b border-dashed border-[#EAEAEB]"}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-1.5">
                      <p className="truncate text-[14px] font-medium text-[#222222]">{vaccineName || item?.name || "Item"}</p>
                      {item === serviceDisplayItem
                        ? serviceOfferCallout && <OfferCalloutBadge callout={serviceOfferCallout} />
                        : item?.gift_id && item?.offer_callout && <OfferCalloutBadge callout={item.offer_callout} />}
                    </div>
                    {isVaccine ? (
                      (item as any)?.vaccination_details ? (
                        <div className="flex items-center gap-1.5 text-[12px] text-[#6D6D6D]">
                          {(item as any).vaccination_details.status && (
                            <PetVaccinationStatus
                              status={(item as any).vaccination_details.status}
                              type="vaccination"
                              className="pointer-events-none no-underline"
                            />
                          )}
                          {(item as any).vaccination_details.due_date ? (
                            <span>{veticDateFormat((item as any).vaccination_details.due_date)}</span>
                          ) : null}
                        </div>
                      ) : null
                    ) : (
                      <p className="line-clamp-2 text-[12px] text-[#6D6D6D]">
                        {item?.description || selectedServiceTypeName || "Cart item"}
                      </p>
                    )}
                    {!completedBookingFlow && item?.opd_charge_text && (
                      <p className="mt-1 text-[12px] text-[#6D6D6D]">{item.opd_charge_text}</p>
                    )}
                  </div>

                  {/* Right: Price/Actions */}
                  <div className="flex justify-between gap-2 flex-col items-end">
                    <div className="flex items-center gap-2">
                      {discountPercent > 0 && <p className="text-[12px] font-medium text-[#239A3D]">{`${discountPercent}%`}</p>}
                      {discountPercent > 0 && <p className="text-[12px] text-[#222222] line-through">{formatAmount(originalPrice)}</p>}
                      <p className="[14px] font-medium text-[#239A3D]">
                        {finalPrice === 0 ? "Free" : formatAmount(finalPrice)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          });
        })()}
      </div>
    </section>
  );
};

export default CartItemsCard;
